import { API, graphqlOperation, Storage, Auth } from 'aws-amplify'
import { getAudit, checklistsByAudit, questionsByAudit, listReviewGroups} from '@/graphql/queries'
import { updateAudit, createQuestionModel, createChecklistModel, updateQuestionModel, updateChecklistModel, deleteQuestionModel, deleteChecklistModel} from '@/graphql/mutations'
import { addReviewer as AddReviewerMutation, createErrorLog, deleteReviewGroup, updateReviewGroup, updateReviewState, updateQuestionResponse  } from '@/graphql/mutations'
import { getUser } from '@/graphql/queries';

const MAX_ITEMS = 500

function copyQuestion(q) {
    let newQ = {
        id: q.id,
        audit: q.audit,
        auditCode: q.auditCode,
        completeDate: q.completeDate,
        desc: q.desc,
        dueDate:q.dueDate,
        result:q.result,
        notes: q.notes,
        imageData: q.imageData,
        response: q.response,
        list: q.list
    }
    return newQ
}

function copyList(l) {
    let newL = {
        id: l.id,
        audit: l.audit,
        auditCode: l.auditCode,
        desc: l.desc,
        list: l.list
    }
    return newL
}

async function logError(func, err) {
    if(err) {
        try {
            err = "" + err + err.stack
            err = err.substring(0, 1024)
        }catch(e) {
            // swallow
        }
    }
    try {
        let input = {
            func: func,
            error: err,
            date: new Date()
        }
        await API.graphql(graphqlOperation(createErrorLog, {input}))
    } catch(err){
        // empty
    }
}
async function updateReviewerLastViewed(auditId, userEmail, lastViewed) {
    try {
        let reviewGroupInput = {
            auditId,
            userEmail,
            lastViewed
        }
        console.log("updating last viewed", reviewGroupInput)
        await API.graphql(graphqlOperation(updateReviewState, reviewGroupInput))
    } catch(err) {
        console.log("problem updating last viewed", err)
    }
}
async function loadReviewers(auditId) {
    try {
        let reviewers = []
        var nextToken, items
        let user = await Auth.currentAuthenticatedUser()
        do {
            ({ data: { listReviewGroups: { items, nextToken } } } = await API.graphql(graphqlOperation(listReviewGroups, {
                auditId: auditId,
                limit: MAX_ITEMS,
                nextToken
            })))
            reviewers.push(...items)
        } while (nextToken)
        return reviewers.map(r => ({
            email: r.userEmail, 
            state: r.state, 
            lastViewed: r.lastViewed, 
            completeDate: r.completeDate,
            addedDate: r.addedDate,
            // migrate old reviewers without role to "reviewer"
            role: r.role ? r.role : "reviewer",
            currentUser: user.attributes.email == r.userEmail
        }))
    }
    catch (err) {
        console.log('error listing reviewers: ', err)
    }
    return []
}

/**
 * Get the key to use for checklists to lookup by parent
 * toplevel checklists have no parent, so we return "TopLevel" 
 * which should not conflict with any actual UUIDs
 */
function getChecklistParent(checklist) {
    if(!checklist.list){
        return "TopLevel"
    }
    return checklist.list
}

async function loadLists(auditId) {
    try {
        let lists = []
        var nextToken, items
        do {
            ({ data: { checklistsByAudit: { items, nextToken } } } = await API.graphql(graphqlOperation(checklistsByAudit, {
                audit: auditId,
                limit: MAX_ITEMS,
                nextToken
            })))
            lists.push(...items)
        } while (nextToken)
        let byParent = lists.reduce((acc, c) => {
            let p = getChecklistParent(c)
            if(!acc[p]) {
                acc[p] = []
            }
            acc[p].push(c)
            return acc;
        }, {})
        return {
            all: lists,
            byParent: byParent
        }
    }
    catch (err) {
        console.log('error listing checklists: ', err)
        logError('loadLists', err)
    }
    return {};
}

async function loadQuestions(auditId) {
    try {
        let questions = []
        var nextToken, items
        do {
            ({ data: { questionsByAudit: { items, nextToken } } } = await API.graphql(graphqlOperation(questionsByAudit, {
                audit: auditId,
                limit: MAX_ITEMS,
                nextToken
            })))
            questions.push(...items)
        } while (nextToken)
        let byParent = questions.reduce((acc, q) => {
            if(!acc[q.list]) {
                acc[q.list] = []
            }
            acc[q.list].push(q) 
            return acc;
        }, {})
        return {
            all: questions,
            byParent: byParent
        }
    }
    catch (err) {
        console.log('error listing questions: ', err)
        logError('loadQuestions', err)
    }
    return {}
}

async function loadOwnerEmail(username) {
    try {
        let data = await API.graphql(graphqlOperation(getUser, {owner: username}))
        console.log("Got ", data, "while loading owner")
        return data.data.getUser.email;
    } catch(err) {
        console.log('error retrieving owner email', err)
    }
}

async function getDocLink(auditId, identityId, file) {
    try {
        return await Storage.get('docs/' + auditId + '/' + file, 
            {level: 'protected', identityId})
    } catch(err) {
        console.log("Error retrieveing url", err)
    }
    return ""
}

/**
 * Return the number of failed questions and total questions that had a response
 * @param {} questions the list of questions to analyze
 */
function getQuestionStats(questions) {
    let totalQs = questions.filter(q => q.result && q.result != "NA");
    let failed = totalQs.filter(q => q.result == "fail");
    return {
        totalQs: totalQs.length, 
        failed: failed.length
    }
}

// Vuex state
const state = {
    header: null,
    lists: [],
    listsByParent: [],
    questions: [],
    questionsByParent: {},
    loading: false,
    reviewers: [],
    filter: null,
    error: null,
    ownerEmail: null,
    attachements:[],
}

// Vuex getters
const getters = {
    topLevelChecklists: (state) => state.listsByParent["TopLevel"],
    checklistsByParent: (state) => (parentId) => {
        let lists = state.listsByParent[parentId]
        if(!lists) {
            lists = []
        }
        return lists
    },
    questionsByParent: (state) => (parentId) => {
        let questions = state.questionsByParent[parentId]
        if (!questions) {
            questions = []
        }
        return questions;
    },
    filteredQuestionsByParent: (state, getters) => 
        (parentId) => getters.questionsByParent(parentId).filter(q => {
            return !state.filter || q.result == state.filter
        }),
    filteredChecklistsByParent: (state, getters) =>
        (parentId) => {
            let unfiltered = getters.checklistsByParent(parentId)
            if( state.filter ){
                return unfiltered.filter(l => getters.filteredQuestionsByParent(l.id).length > 0 || 
                                        getters.filteredChecklistsByParent(l.id).length > 0 )
            } else {
                return unfiltered
            }
        },
    filteredTopLevelChecklists: (state, getters) => {
        let unfiltered = getters.topLevelChecklists
        if ( state.filter ) {
            return unfiltered.filter(l => getters.filteredQuestionsByParent(l.id).length > 0 || 
                                    getters.filteredChecklistsByParent(l.id).length > 0 )
        } else {
            return unfiltered
        }
    },
        
    loading: state => state.loading,
}

// Vuex actions
const actions = {
    async setCurrent({ commit, dispatch }, audit) {

        let header = {}
        Object.assign(header, audit)
        console.log("header", header)
        
        let reviewerPromise = loadReviewers(header.id)
        try {
            if( !header.facilityName ) {
                let d  = await API.graphql(graphqlOperation(getAudit, {id:header.id}))
                console.log(d)
                header = d.data.getAudit
                commit('setError', null)
            }
        } catch(err) {
            console.log("Error loading audit", err)
            logError('setCurrent', err)
            if (err.errors && err.errors.length > 0) {
                console.log("error while loading audit", err)
                if (err.errors[0].errorType== "Unauthorized") {
                    // use a new string so watchers can tell that the property chagned
                    // if we use a string literal, the runtime interns it and it looks like the prop doesn't change
                    commit('setError', new String("Unauthorized"))
                }
            }
        }
        let reviewers = await reviewerPromise
        commit('setReviewers', reviewers)

        if (header && header.owner) {
            let ownerEmail = await loadOwnerEmail(header.owner)            
            if (ownerEmail)
                commit('setOwnerEmail', ownerEmail)
        }
        commit('setHeader', header)
        commit('setLoading', true)

        if(header) {
            let checklistPromise = loadLists(header.id)

            let questionPromise = loadQuestions(header.id)
    
    
            let lists = await checklistPromise
            let questions = await questionPromise
            commit('setFilter', null)
            commit('setLists', lists)
            commit('setQuestions', questions)
        }
        dispatch('loadDocLinks')
        commit('setLoading', false)
        dispatch('updateLastViewed')
    },

    async updateLastViewed({state}) {
        if(!state.error) {
            let user = await Auth.currentAuthenticatedUser()
            if( state.header && state.header.state == "in-review" && user && user.attributes && user.attributes.email) {
                if(state.reviewers.length == 1 && state.reviewers[0].email == user.attributes.email){
                    updateReviewerLastViewed(state.header.id, user.attributes.email, new Date())
                }
            }
        }
    },

    async newChecklist({ commit }, list) {
        await API.graphql(graphqlOperation(createChecklistModel, { input: list }))
        commit('addList', list)
    },

    async newQuestion({ commit }, question) {
        await API.graphql(graphqlOperation(createQuestionModel, { input: question }))
        commit('addQuestion', question)
    },

    async updateList({ commit }, {item, updates}) {
        let newList = copyList(item)
        updates.forEach((up) => {
            newList[up.field] = up.value
        })
        console.log("updating list", newList)
        await API.graphql(graphqlOperation(updateChecklistModel, {input:newList}))
        commit('updateList', { item, updates} )
    },

    async updateQuestion({ commit }, {item, updates}) {
        let newQuestion = copyQuestion(item)
        updates.forEach((up) => {
            newQuestion[up.field] = up.value
        })
        console.log("Saving question", newQuestion)
        await API.graphql(graphqlOperation(updateQuestionModel, {input:newQuestion}))
        commit('updateQuestion', { item, updates } )
    },

    async updateQuestions({ commit }, {questions, field, value}) {
        questions.forEach(async function(question){
            let newQuestion = copyQuestion(question)
            newQuestion[field] = value
            await API.graphql(graphqlOperation(updateQuestionModel, {input:newQuestion}))
        })
        commit('updateQuestions', {questions, field, value})
    },

    async updateQuestionResponse({ commit }, {question, field, value}) {
        let newQuestion = {}
        newQuestion.questionId = question.id
        newQuestion.response = value
        await API.graphql(graphqlOperation(updateQuestionResponse, newQuestion))
        let updates = [];
        updates.push({field, value})
        commit('updateQuestion', { item:question, updates} )
    },

    async updateQuestionResponseCompleteDate({ commit }, {question, field, value}) {
        let newQuestion = {}
        newQuestion.questionId = question.id
        newQuestion.completeDate = value
        await API.graphql(graphqlOperation(updateQuestionResponse, newQuestion))
        let updates = [];
        updates.push({field, value})
        commit('updateQuestion', { item:question, updates} )
    },

    async deleteQuestion({ commit }, question) {
        await API.graphql(graphqlOperation(deleteQuestionModel, {input:{id:question.id}}))
        commit('deleteQuestion', question)
    },

    async deleteQuestions({ commit }, questions) {
        let promises = questions.map(async (q) => {
            await API.graphql(graphqlOperation(deleteQuestionModel, {input: {id:q.id}}))
            commit('deleteQuestion', q)
        })
        
        promises.map((p) => p.then(() => commit('deleteQuestion', )))
    },

    async deleteList({commit}, list) {
        await API.graphql(graphqlOperation(deleteChecklistModel , {input:{id:list.id}}))
        commit('deleteList', list)
    },

    async setState({ commit }, newState) {
        let header = state.header
        let newReviewState = null

        if(newState == 'review-complete') {
            newState = 'in-review'
            newReviewState = 'complete'
        }

        try {
            if( header.state != newState || header.reviewState != newReviewState) {
                let newHeader = {
                    id: header.id,
                    state: newState,
                    reviewState: newReviewState
                }
                if( newHeader.state=="in-review") {
                    // in progress => in review
                    if( header.state =="inprogress") {
                        newHeader.inReviewDate = new Date();
                        let stats = getQuestionStats(state.questions)
                        newHeader.failedReview = stats.failed;
                        newHeader.totalQs = stats.totalQs;
                    } else if (header.reviewState == "complete") {
                        console.log("setting review state back to null")
                        state.reviewers.forEach(async function(r){
                            // only owner should be doing this, so can use the updateReviewGroup mutation
                            try {
                                await API.graphql(graphqlOperation(updateReviewGroup, { input: {
                                    auditId: newHeader.id, 
                                    userEmail:r.email,
                                    completeDate: null, 
                                    state:""}
                                }))
                            }catch(err) {
                                console.log("error updating review state", err)
                            }
                        })
                    }
                    // review complete => in-review
                } else if ( newHeader.state == "complete" && header.state != "complete") {
                    newHeader.completeDate = new Date();
                }
                if( newHeader.reviewState == "complete") {
                    newHeader.reviewCompleteDate = new Date()
                }
                await API.graphql(graphqlOperation(updateAudit, {input:newHeader}))
                commit('setState', newState)
            }
        } catch(err) {
            console.log("Error updating state", err)
            logError("Audit.setState", err)
        }
    },

    async setReviewState({ commit }, {newReviewState, userEmail}) {
        let header = state.header
        if( header.state == 'in-review') {
            let newHeader = {}
            Object.assign(newHeader, header)
            newHeader.reviewState = newReviewState
            if( newHeader.reviewState == "complete") {
                newHeader.reviewCompleteDate = new Date()
            }
            //console.log("Updating review in the cloud with", newHeader)
            //await API.graphql(graphqlOperation(updateAudit, {input:newHeader}))
            commit('setReviewState', newReviewState)
            let reviewGroupInput = {
                auditId: state.header.id, 
                userEmail:userEmail,
                completeDate: new Date(), 
                state:newReviewState
            }
            await API.graphql(graphqlOperation(updateReviewState, reviewGroupInput))
            commit('setReviewerState', {email: reviewGroupInput.userEmail, reviewState: reviewGroupInput.state})
        }
    },

    async addReviewer({ commit }, reviewer) {
        let input = {
            auditId: state.header.id,
            userEmail: reviewer.email,
            role: reviewer.role,
            addedDate: new Date()
        }
        try {
            await API.graphql(graphqlOperation(AddReviewerMutation, input))
            commit('addReviewer', {email:input.userEmail, role:input.role, addedDate:input.addedDate})
        } catch(err) {
            console.log("error adding reviewer", err)
        }
    },

    async deleteReviewer({commit}, reviewer) {
        let input = {
            auditId: state.header.id,
            userEmail: reviewer
        }
        try {
            await API.graphql(graphqlOperation(deleteReviewGroup, {input}))
            commit('deleteReviewer', reviewer)
        } catch(err) {
            console.log("error deleting reviewer", err)
        }

    },

    async updateFacilityName( { commit }, newFacName) {
        if (newFacName && newFacName != state.header.facilityName) {
            let newHeader = {
                id: state.header.id,
                facilityName: newFacName
            } 
            await API.graphql(graphqlOperation(updateAudit, {input: newHeader}))
            commit('setFacilityName', newFacName)
        }
    },

    async updateMgrName( { commit }, newMgrName) {
        if (newMgrName && newMgrName != state.header.MgrName) {
            let newHeader = {
                id: state.header.id,
                mgrName: newMgrName
            } 
            await API.graphql(graphqlOperation(updateAudit, {input: newHeader}))
            commit('setMgrName', newMgrName)
        }
    },

    async setFilter({ state, commit }, filter) {
        if(state.filter != filter)
            commit('setFilter', filter)
    },

    async attachFile({ state, commit, dispatch}, file) {
        let { identityId } = await Auth.currentCredentials()

        let fileName = 'docs/' + state.header.id + '/' + file.name
        try {
            await Storage.put(fileName, file, {level: 'protected'})
            let header = state.header
            let newHeader = {id:header.id}
            if(!header.documents)
                newHeader.documents = []
            else
                newHeader.documents = header.documents.slice()
            newHeader.documents.push({identityId, file:file.name})
            await API.graphql(graphqlOperation(updateAudit, {input:newHeader}))

            commit('attachFile', {identityId, filename:file.name})

            dispatch('loadDocLinks')
        } catch(err) {
            console.log("error during upload", err)
        }
    },

    async deleteAttachement({state, commit, dispatch}, file) {
        let fileName = 'docs/' + state.header.id + '/' + file.file
        try {
            let header = state.header
            let newHeader = {}
            Object.assign(newHeader, header)
            if(!newHeader.documents) {
                newHeader.documents = []
            }
            else {
                newHeader.documents = header.documents.slice()
            }
            let idx = newHeader.documents.findIndex(e => e.file == file.file)
            if( idx != -1) {
                newHeader.documents.splice(idx, 1)
                await Storage.remove(fileName, {level: 'protected'})
                await API.graphql(graphqlOperation(updateAudit, {input:newHeader}))
    
                commit('deleteAttachement', file.file)
    
                dispatch('loadDocLinks')
                }
        } catch(err) {
            console.log("error while deleting file", err)
        }
    },

    async loadDocLinks({state, commit}) {
        let auditId = state.header.id
        if(state.header.documents) {
            let urlPromises = state.header.documents.map(async(f) => {
                let url = await getDocLink(auditId, f.identityId, f.file)
                return {
                    identityId: f.identityId,
                    file: f.file,
                    url
                }
            } );
            let docs = []
            for( var idx = 0; idx < urlPromises.length; ++idx in urlPromises ) {
                let obj = await urlPromises[idx];
                console.log("p", urlPromises[idx], obj)
                docs.push(await obj)
            }
            commit('setAttachements', docs)            
        }
    },

    async recordError(_, {name, error}) {
        await logError(name, error);
    }

}

// Vuex mutations
const mutations = {
    setHeader(state, header) {
        state.header = header
        console.log("Got new header", header)
    },
    setOwnerEmail(state, email) {
        console.log("Setting owner email to ", email)
        state.ownerEmail = email
    },
    setLists(state, lists) {
        state.lists = lists.all
        state.listsByParent = lists.byParent
        console.log("got new checklists", lists)
    },
    setQuestions(state, questions) {
        state.questions = questions.all
        state.questionsByParent = questions.byParent
        console.log("Got new questions", questions)
    },
    addList(state, list) {
        state.lists.push(list)
        let parent = getChecklistParent(list)
        let byParent = state.listsByParent[parent]
        if(!byParent) {
            byParent = {}
            Object.assign(byParent, state.listsByParent)
            byParent[parent] = [list]
            state.listsByParent = byParent
        } else {
            byParent.push(list)
        }
    },
    addQuestion(state, question) {
        state.questions.push(question)
        let byParent = state.questionsByParent[question.list]
        console.log("ByParent for adding question", byParent, question)
        if(!byParent) {
            byParent = {}
            Object.assign(byParent, state.questionsByParent)
            byParent[question.list] = [question]
            state.questionsByParent = byParent
        } else {
            byParent.push(question)
        }
    },
    updateList(state, { item, updates }) {
        updates.forEach(up => {
            item[up.field] = up.value
        })
    },
    updateQuestion(state, { item, updates }) {
        updates.forEach(up => {
            item[up.field] = up.value
        })
    },
    updateQuestions(state, { questions, field, value}) {
        questions.forEach(q => {
            q[field] = value
        })
    },
    deleteQuestion(state, question) {
        let idx = state.questions.indexOf(question)
        if (idx > -1) {
            state.questions.splice(idx, 1)
        }
        let byParent = state.questionsByParent[question.list]
        if(byParent) {
            idx = byParent.indexOf(question)
            if (idx > -1) {
                byParent.splice(idx,1)
            }
        }
    },
    deleteList(state, list) {
        let idx = state.lists.indexOf(list)
        if( idx > -1) {
            state.lists.splice(idx, 1)
        }
        let byParent = state.listsByParent[getChecklistParent(list)]
        if(byParent) {
            idx = byParent.indexOf(list)
            if(idx > -1) {
                byParent.splice(idx, 1)
            }
        }
    },
    setState(state, newState) {
        state.header.state = newState
    },

    setReviewState(state, newReviewState) {
        state.header.reviewState = newReviewState
    },

    setFacilityName(state, newFacName) {
        state.header.facilityName = newFacName
    },

    setMgrName(state, newMgrName) {
        state.header.mgrName = newMgrName
    },

    setLoading(state, value) {
        console.log("Setting loading to", value)
        state.loading = value
    },

    /**
     * Reviewer related
     */
    addReviewer(state, newReviewer) {
        console.log("newReviewer is", newReviewer)
        const idx = state.reviewers.findIndex(e => e.email == newReviewer.email)
        if(idx == -1) {
            state.reviewers.push(newReviewer)
        }
    },

    deleteReviewer(state, reviewer) {
        const idx = state.reviewers.findIndex(e => e.email == reviewer)
        if(idx != -1) {
            state.reviewers.splice(idx, 1)
        }
    },
    setReviewerState(state, {email, reviewState}) {
        const idx = state.reviewers.findIndex(e => e.email == email)
        console.log("setReviewerState", email, reviewState)
        if( idx != -1) {
            console.log("setting review state", email, reviewState)
            state.reviewers[idx] = {email:email, state: reviewState}
        }
    },

    setReviewers(state, reviewers) {
        state.reviewers = reviewers
        console.log("Reviewers", reviewers)
    },

    /**
     * filtering
     */
    setFilter(state, newFilter) {
        state.filter = newFilter
    },

    /**
     * errors 
     */
    setError(state, error) {
        console.log("setting error to",error)
        state.error = error
    },

    /**
     * Documents
     */
    attachFile(state, {identityId, filename}) {
        console.log("Attaching new file", filename)
        let docs = state.header.documents 
        if( !docs )
            docs = []

        docs.push({identityId, file:filename});

        state.header.documents = docs;
    },

    deleteAttachement(state, att) {
        let docs = state.header.documents
        if(docs) {
            let idx = docs.findIndex(e => e.file == att)
            if( idx != -1 ) {
                docs.splice(idx, 1)
                state.header.documents = docs
            }

        }
    },

    setAttachements(state, att) {
        state.attachements = att
    },
}

export default {
    namespaced: true,
    state,
    actions,
    mutations,
    getters
}
